import {
  Alert,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  OrganizationState,
  useOrganizationState,
} from 'features/organizations/state/useOrganizationState';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useOrganizationPricing } from 'features/organizations/hooks/useOrganizationPricing';
import { useAuthState } from 'state/useAuthState';
import { getOrgCommonSignupFormsValidationSchema } from 'features/organizations/organizations.utils';
import { Link } from 'react-router-dom';
import { EmailStatus } from 'shared/getEmailValidationSchema';
import { OrganizationPurchaseSummary } from '../OrganizationPurchaseSummary';
import { OrganizationSeatsFormFields } from '../OrganizationSeatsFormFields/OrganizationSeatsFormFields';

type OrganizationAccountInfoData = {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  proSeats: number;
  regularSeats: number;
  additionalEmails?: string;
  poNumber?: string;
};

type OrganizationAccountInfoFormProps = {
  onSubmit: (values: Partial<OrganizationState>) => void;
};

export const OrganizationAccountInfoForm = (
  props: OrganizationAccountInfoFormProps,
): JSX.Element => {
  const { onSubmit } = props;
  const { t } = useTranslation();
  const { pricing } = useOrganizationPricing();
  const {
    companyName,
    firstName,
    lastName,
    email,
    proSeats,
    regularSeats,
    poNumber,
  } = useOrganizationState();
  const { user } = useAuthState();

  const { control, handleSubmit, watch, formState } =
    useForm<OrganizationAccountInfoData>({
      resolver: yupResolver(
        getOrgCommonSignupFormsValidationSchema({
          t,
          requireExistingUserToLogin: Boolean(user),
          emailValidationCache: new Map<string, EmailStatus>(),
          fields: [
            'companyName',
            'firstName',
            'lastName',
            'email',
            'proSeats',
            'regularSeats',
            'poNumber',
          ],
        }),
      ),
      defaultValues: {
        companyName: '',
        firstName: '',
        lastName: '',
        email: '',
        proSeats: 0,
        regularSeats: 0,
        poNumber: undefined,
      },
      values: {
        companyName: companyName || '',
        firstName: firstName || '',
        lastName: lastName || '',
        email: email || '',
        proSeats: proSeats || 0,
        regularSeats: regularSeats || 0,
        poNumber,
      },
      mode: 'onBlur',
    });

  const { errors } = formState;

  const watchedRegularSeats = watch('regularSeats', regularSeats);
  const watchedProSeats = watch('proSeats', proSeats);

  const handleFormSubmit = (data: OrganizationAccountInfoData): void => {
    onSubmit(data);
  };

  return (
    <Stack
      spacing={2}
      sx={{ flex: 1, pt: 1 }}
      component="form"
      noValidate
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <Stack spacing={2}>
        {errors.email && errors.email.type === 'registered' && (
          <Grid>
            <Alert icon={false} severity="error" sx={{ color: 'error.main' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'medium',
                  marginBottom: '16px',
                }}
              >
                {errors.email.message}
              </Typography>

              <MuiLink
                to="/login"
                component={Link}
                color="inherit"
                underline="always"
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {t('membershipCheckout.login')}
              </MuiLink>
            </Alert>
          </Grid>
        )}
        <Grid>
          <Controller
            name="companyName"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.companyName.label')}
                error={Boolean(errors.companyName)}
                helperText={errors.companyName?.message}
                required
                {...field}
              />
            )}
          />
        </Grid>
        <Grid container spacing={2} direction="row">
          <Grid size={6}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label={t('organizationPurchase.firstName.label')}
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName?.message}
                  required
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label={t('organizationPurchase.lastName.label')}
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName?.message}
                  required
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.email.label')}
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                disabled={Boolean(user?.email)}
                required
                {...field}
              />
            )}
          />
        </Grid>
        <Grid>
          <Controller
            name="poNumber"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.poNumber.label')}
                error={Boolean(errors.poNumber)}
                helperText={errors.poNumber?.message}
                {...field}
                value={field.value || ''}
              />
            )}
          />
        </Grid>

        <Grid>
          <Typography variant="h2">{t('organization.featureName')}</Typography>
        </Grid>

        <OrganizationSeatsFormFields
          control={control}
          formState={formState}
          pricing={pricing}
        />

        <OrganizationPurchaseSummary
          regularSeats={watchedRegularSeats}
          proSeats={watchedProSeats}
        />
      </Stack>

      <Stack sx={{ flexGrow: 1, justifyContent: 'flex-end' }}>
        <Button
          size="large"
          fullWidth
          type="submit"
          disabled={!formState.isValid}
        >
          {t('organizationPurchase.continue')}
        </Button>
      </Stack>
    </Stack>
  );
};
